import { all, call, put, select, takeLeading } from 'redux-saga/effects';
import type { PaymentIntentResult, StripeError } from '@stripe/stripe-js';
import { Url } from '@wix/da-url';
import { PapiCheckout, PapiDacheckoutPurchase } from '@wix/da-papi-types';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import {
  confirmStripeOrder,
  stripeExpressButtonClicked,
  setErrorCode,
  setStripeErrorMessage,
} from '../actions/checkout';
import { placeOrder } from './helpers';
import { getCheckout } from '../selectors/checkout';
import { getPayBiData } from '../helpers/checkout';
import { expressPaymentTypeToPaymentMethod } from '../helpers/stripe';

function* handleConfirmStripeOrder(
  action: ReturnType<typeof confirmStripeOrder>
) {
  const { stripe, elements, event } = action.payload;
  const { error: submitError }: { error?: StripeError } = yield call(
    elements.submit
  );
  if (submitError) {
    yield put(setStripeErrorMessage(submitError.message!));
    return;
  }
  const purchaseResult: PapiDacheckoutPurchase = yield call(
    placeOrder,
    expressPaymentTypeToPaymentMethod(event.expressPaymentType)
  );

  if (purchaseResult.errorCode) {
    yield put(setErrorCode(purchaseResult.errorCode));
    event.paymentFailed({ reason: 'fail' });
    return;
  }
  const confirmResult: PaymentIntentResult = yield call(stripe.confirmPayment, {
    elements,
    clientSecret: purchaseResult.stripeClientSecret!,
    confirmParams: {
      return_url: Url.checkoutCompleteLink(
        purchaseResult.orderConfirmation!.ordergroupId
      ),
    },
  });
  if (confirmResult.error) {
    yield put(setStripeErrorMessage(confirmResult.error.message!));
    return;
  }
  // If confirmation is succesful, user will be taken to `return_url`
}

function* handleClick(action: ReturnType<typeof stripeExpressButtonClicked>) {
  const checkout: PapiCheckout = yield select(getCheckout);
  const hasCoreMembership: boolean = yield select(getCurrentUserHasCore);
  yield call(
    logBiEvent,
    getPayBiData(
      checkout.order,
      expressPaymentTypeToPaymentMethod(
        action.payload.event.expressPaymentType
      ),
      hasCoreMembership,
      checkout.giftUser
    )
  );
}

export default function* stripeSaga() {
  yield all([
    takeLeading(stripeExpressButtonClicked, handleClick),
    takeLeading(confirmStripeOrder, handleConfirmStripeOrder),
  ]);
}
